"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { Eye, EyeOff, ExternalLink } from 'lucide-react'
import {
  clearStoredPassword,
  readStoredPassword,
  writeStoredPassword,
} from './passwordStorage'
import {
  BreakdownTable,
  DEFAULT_SORT,
  SORT_KEYS,
  type SortKey,
  type SortState,
} from './BreakdownTable'
import { DailyBarChart } from './DailyBarChart'
import { TimelineRangeScrubber } from './TimelineRangeScrubber'
import { isPlainLeftClick } from './plainLeftClick'
import {
  analyticsRangeKey,
  ANALYTICS_SITES,
  DEFAULT_ANALYTICS_SITE_ID,
  getAnalyticsSite,
  HISTORY_CHUNK_DAYS,
  isAnalyticsDate,
  localTodayIso,
  MAX_ANALYTICS_RANGE_DAYS,
  rangeDayCount,
  type AnalyticsRange,
  type AnalyticsResponse,
  type AnalyticsSite,
  type AnalyticsSiteId,
  type AnalyticsSummary,
  type AnalyticsTier,
  type HistoryDailyPoint,
} from './types'

const POSTHOG_UI_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? 'https://us.posthog.com'

const fetchAnalytics = async (
  password: string,
  site: AnalyticsSiteId,
  range: AnalyticsRange,
  tier: AnalyticsTier,
): Promise<
  | { ok: true; data: AnalyticsResponse }
  | { ok: false; status: number; message: string }
> => {
  const rangeBody =
    range.kind === 'days'
      ? { days: range.days }
      : { start: range.start, end: range.end }
  const res = await fetch('/api/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, site, tier, ...rangeBody }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    return { ok: false, status: res.status, message: body?.error || res.statusText }
  }
  return { ok: true, data: (await res.json()) as AnalyticsResponse }
}

// A PostHog "too many concurrent queries" rejection, as it surfaces in the
// route's posthogError string after the server's own retries ran out. The
// client waits and re-requests these instead of pinning the error banner.
const isThrottledError = (message: string | null | undefined) =>
  !!message && (message.includes('(429)') || message.includes('throttled'))

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Client-side waits between re-requests of a throttled tier/chunk. Long on
// purpose — the server already retried with backoff for ~15s before this
// error reached us, so PostHog is genuinely saturated and needs breathing
// room, not more traffic.
const THROTTLE_RETRY_DELAYS_MS = [5_000, 15_000]

// Full-page gate shown until a password is accepted. Same password-manager
// affordances as the editor's PasswordModal (hidden username, autocomplete
// hints, show/hide toggle), but rendered as an inline centered card — a
// dedicated page has nothing to "cancel" back to.
const PasswordGate = ({
  onSubmit,
}: {
  onSubmit: (password: string) => Promise<{ ok: boolean; message?: string }>
}) => {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password) return
    setIsSubmitting(true)
    setError(null)
    try {
      const result = await onSubmit(password)
      if (!result.ok) setError(result.message || 'Incorrect password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <form
        className="flex w-[360px] flex-col gap-2 border border-neutral-200 bg-white p-6"
        onSubmit={handleSubmit}
        action="#"
        method="post"
      >
        <h1 className="mb-0 text-base font-semibold">Site analytics</h1>
        <p className="text-sm text-[#666]">Password required.</p>
        <input
          type="text"
          name="username"
          autoComplete="username"
          value="analytics"
          readOnly
          hidden
          aria-hidden="true"
        />
        <div className="flex items-center border border-neutral-300">
          <input
            type={isVisible ? 'text' : 'password'}
            name="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="min-w-0 flex-1 px-2 py-1 font-mono text-sm outline-none"
            placeholder="ANALYTICS_PASSWORD"
          />
          <button
            type="button"
            onClick={() => setIsVisible(v => !v)}
            className="px-2 py-1 text-neutral-500 hover:text-neutral-800"
            aria-label={isVisible ? 'Hide password' : 'Show password'}
          >
            {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
        <button
          type="submit"
          disabled={!password || isSubmitting}
          className="mt-1 bg-black px-2 py-1.5 text-xs text-white disabled:opacity-50"
        >
          {isSubmitting ? '…' : 'View analytics'}
        </button>
      </form>
    </div>
  )
}

const SummaryCard = ({
  label,
  value,
  sub,
}: {
  label: string
  value: number
  sub?: string
}) => (
  <div className="border border-neutral-200 bg-white px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-[#888]">{label}</div>
    <div className="mt-0.5 flex items-baseline gap-2">
      <span className="text-xl font-semibold tabular-nums leading-none">
        {value.toLocaleString()}
      </span>
      {sub && <span className="text-[11px] tabular-nums text-[#2A623D]">{sub}</span>}
    </div>
  </div>
)

// Placeholder for a below-the-fold breakdown table while its data streams in.
// Same card chrome as BreakdownTable with a few shimmer rows, so the layout
// doesn't jump when the real panel replaces it.
const PanelSkeleton = ({ title }: { title: string }) => (
  <section className="border border-neutral-200 bg-white p-4">
    <h2 className="mb-3 mt-0 text-sm font-semibold text-[#888]">{title}</h2>
    <ul className="flex flex-col gap-1.5" aria-hidden="true">
      {[90, 70, 55, 40, 30].map(width => (
        <li
          key={width}
          className="h-4 animate-pulse rounded-sm bg-neutral-100"
          style={{ width: `${width}%` }}
        />
      ))}
    </ul>
  </section>
)

// Placeholder matching SummaryCard's footprint, shown per card until core lands.
const SummaryCardSkeleton = ({ label }: { label: string }) => (
  <div className="border border-neutral-200 bg-white px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-[#888]">{label}</div>
    <div
      className="mt-1 h-5 w-16 animate-pulse rounded-sm bg-neutral-100"
      aria-hidden="true"
    />
  </div>
)

// The four summary-card labels, so the loading and loaded states stay in sync.
const SUMMARY_CARD_LABELS = [
  'Pageviews',
  'Unique visitors',
  'Pageviews / day',
  'Views / visitor',
] as const

const DEFAULT_DAYS = 30
const DEFAULT_RANGE: AnalyticsRange = { kind: 'days', days: DEFAULT_DAYS }

// The breakdown panels whose sort state lives in the dashboard (and the
// URL). Ids double as the URL token: `?sort=pages-duration-desc`.
const PANEL_IDS = [
  'pages',
  'planExits',
  'referrers',
  'devices',
  'hovers',
  'hudClicks',
] as const
type PanelId = (typeof PANEL_IDS)[number]
type PanelSorts = Record<PanelId, SortState>

const defaultSorts = (): PanelSorts =>
  Object.fromEntries(PANEL_IDS.map(id => [id, DEFAULT_SORT])) as PanelSorts

const isPanelId = (value: string): value is PanelId =>
  (PANEL_IDS as readonly string[]).includes(value)
const isSortKey = (value: string): value is SortKey =>
  (SORT_KEYS as readonly string[]).includes(value)

// Everything the URL can carry: ?site=ai-2027, ?days=90 OR ?start=…&end=…,
// and one repeated ?sort=<panel>-<column>-<dir> per non-default panel sort.
// Invalid or missing params fall back to null / {} so a hand-mangled URL
// degrades to the defaults instead of breaking the page.
const parseUrlState = (
  search: string,
): {
  site: AnalyticsSiteId | null
  range: AnalyticsRange | null
  sorts: Partial<PanelSorts>
} => {
  const params = new URLSearchParams(search)
  const site = getAnalyticsSite(params.get('site'))?.id ?? null

  let range: AnalyticsRange | null = null
  const start = params.get('start')
  const end = params.get('end')
  if (isAnalyticsDate(start) && isAnalyticsDate(end) && start <= end) {
    const candidate: AnalyticsRange = { kind: 'custom', start, end }
    if (rangeDayCount(candidate) <= MAX_ANALYTICS_RANGE_DAYS) range = candidate
  } else {
    const days = Number(params.get('days'))
    if (Number.isInteger(days) && days >= 1 && days <= MAX_ANALYTICS_RANGE_DAYS) {
      range = { kind: 'days', days }
    }
  }

  const sorts: Partial<PanelSorts> = {}
  for (const token of params.getAll('sort')) {
    const [panel, key, dir] = token.split('-')
    if (isPanelId(panel) && isSortKey(key) && (dir === 'asc' || dir === 'desc')) {
      sorts[panel] = { key, dir }
    }
  }
  return { site, range, sorts }
}

// Inverse of parseUrlState: defaults are omitted so the pristine dashboard
// keeps a bare /analytics URL.
const buildUrlSearch = (
  site: AnalyticsSiteId,
  range: AnalyticsRange,
  sorts: PanelSorts,
): string => {
  const params = new URLSearchParams()
  if (site !== DEFAULT_ANALYTICS_SITE_ID) params.set('site', site)
  if (range.kind === 'custom') {
    params.set('start', range.start)
    params.set('end', range.end)
  } else if (range.days !== DEFAULT_DAYS) {
    params.set('days', String(range.days))
  }
  for (const id of PANEL_IDS) {
    const { key, dir } = sorts[id]
    if (key !== DEFAULT_SORT.key || dir !== DEFAULT_SORT.dir) {
      params.append('sort', `${id}-${key}-${dir}`)
    }
  }
  return params.toString()
}

// Per-site load state. `summary` accumulates as tier responses arrive (each
// folded on top of what's there), and the render checks per-panel presence
// (summary.totals, summary.topPages, summary.hovers…) so every panel shows
// real data the moment its own tier lands. `engagementLoaded` records that the
// pageview tables carry their engagement columns — it stops a slow light
// `tables` response from clobbering already-enriched rows, and drives the
// columns' pending shimmer. `error` holds the site's setup notice / query
// failure. Kept per-site so switching tabs shows whatever that site has
// already loaded instead of refetching from zero.
type SiteEntry = {
  summary: Partial<AnalyticsSummary> | null
  error: string | null
  engagementLoaded: boolean
}

const EMPTY_ENTRY: SiteEntry = {
  summary: null,
  error: null,
  engagementLoaded: false,
}

const emptySiteData = (): Record<AnalyticsSiteId, SiteEntry> =>
  Object.fromEntries(ANALYTICS_SITES.map(s => [s.id, EMPTY_ENTRY])) as Record<
    AnalyticsSiteId,
    SiteEntry
  >

const emptyHistory = (): Record<AnalyticsSiteId, HistoryDailyPoint[] | null> =>
  Object.fromEntries(ANALYTICS_SITES.map(s => [s.id, null])) as Record<
    AnalyticsSiteId,
    HistoryDailyPoint[] | null
  >

// The site's whole life (launch → today) as a series of custom ranges, each
// spanning at most HISTORY_CHUNK_DAYS — the chunks of the timeline scrubber's
// history sweep. Newest first, so recent readership lands on the scrubber
// immediately and the graph grows leftward toward launch.
const historyChunkRanges = (launched: string): AnalyticsRange[] => {
  const DAY_MS = 86_400_000
  const launchMs = Date.parse(`${launched}T00:00:00Z`)
  let endMs = Date.parse(`${localTodayIso()}T00:00:00Z`)
  const chunks: AnalyticsRange[] = []
  while (endMs >= launchMs) {
    const startMs = Math.max(launchMs, endMs - (HISTORY_CHUNK_DAYS - 1) * DAY_MS)
    chunks.push({
      kind: 'custom',
      start: new Date(startMs).toISOString().slice(0, 10),
      end: new Date(endMs).toISOString().slice(0, 10),
    })
    endMs = startMs - DAY_MS
  }
  return chunks
}

export const AnalyticsDashboard = () => {
  // null password = show the gate. hasBooted avoids flashing the gate while
  // the stored password is read (and dodges an SSR/localStorage mismatch).
  const [password, setPassword] = useState<string | null>(null)
  const [hasBooted, setHasBooted] = useState(false)
  // Which website's analytics the tab bar is showing. Each site is a separate
  // PostHog project; its data lives under siteData[siteId].
  const [siteId, setSiteId] = useState<AnalyticsSiteId>(DEFAULT_ANALYTICS_SITE_ID)
  const [range, setRange] = useState<AnalyticsRange>(DEFAULT_RANGE)
  // Per-panel table ordering, lifted out of BreakdownTable so it can be
  // mirrored into the URL alongside the site and range.
  const [sorts, setSorts] = useState<PanelSorts>(defaultSorts)
  const [siteData, setSiteData] =
    useState<Record<AnalyticsSiteId, SiteEntry>>(emptySiteData)

  // Every fetch is stamped with the range it was issued for; a response is
  // applied only if that range still matches the ref. Changing the range or
  // locking bumps the ref, so in-flight responses for a stale range/session are
  // dropped instead of overwriting fresh data — the guard the old single-fetch
  // path lacked, and which matters now that several fetches fly concurrently.
  // (The site isn't part of the guard: background prefetches for the inactive
  // site must still apply while a different tab is showing.)
  const requestKeyRef = useRef(`:${analyticsRangeKey(DEFAULT_RANGE)}`)

  // Full-history daily pageviews per site — the readership graph behind the
  // timeline scrubber. Range-independent (always launch → today), so it lives
  // OUTSIDE siteData and survives the range-change resets; swept once per
  // site per unlocked session, in chunks, after the initial panel load
  // settles. historyStartedRef marks sites whose sweep has begun, so the
  // loadEverything re-runs on every range change can't kick it off again.
  const [historyBySite, setHistoryBySite] = useState<
    Record<AnalyticsSiteId, HistoryDailyPoint[] | null>
  >(emptyHistory)
  const historyStartedRef = useRef<Set<AnalyticsSiteId>>(new Set())

  // Drop back to the password gate and invalidate every in-flight fetch. Bumping
  // the ref to a value no outstanding request was stamped with means their late
  // responses fail the `endsWith` guard and are discarded. Shared by the Lock
  // button and the 401 path, so both can't be re-populated by a slow response.
  const lock = useCallback(() => {
    requestKeyRef.current = 'locked'
    clearStoredPassword()
    setPassword(null)
    setSiteData(emptySiteData())
    setHistoryBySite(emptyHistory())
    historyStartedRef.current = new Set()
  }, [])

  // Fetch one tier for one site and fold the result into siteData. Returns the
  // auth outcome so the gate can react to a 401. Stale responses (range
  // changed / logged out) are discarded. A throttled PostHog answer is retried
  // after a wait rather than folded in — the error banner is for persistent
  // failures, not a momentarily busy query queue.
  const fetchTier = useCallback(
    async (
      candidatePassword: string,
      site: AnalyticsSiteId,
      fetchRange: AnalyticsRange,
      tier: AnalyticsTier,
    ): Promise<{ ok: boolean; status?: number; message?: string }> => {
      let result: Awaited<ReturnType<typeof fetchAnalytics>>
      let attempt = 0
      for (;;) {
        result = await fetchAnalytics(candidatePassword, site, fetchRange, tier)

        // Drop the response if the user has since changed range or locked.
        const stillCurrent = requestKeyRef.current.endsWith(
          `:${analyticsRangeKey(fetchRange)}`,
        )
        if (!stillCurrent) return { ok: result.ok }

        if (!result.ok) {
          if (result.status === 401) lock()
          return { ok: false, status: result.status, message: result.message }
        }

        if (
          !isThrottledError(result.data.posthogError) ||
          attempt >= THROTTLE_RETRY_DELAYS_MS.length
        ) {
          break
        }
        await sleep(THROTTLE_RETRY_DELAYS_MS[attempt])
        attempt += 1
      }

      const { posthog, posthogError } = result.data
      // These tiers deliver the pageview tables WITH their engagement columns.
      const enrichesTables =
        tier === 'engagement' || tier === 'rest' || tier === 'full'
      setSiteData(prev => {
        const entry = prev[site]
        // Merge onto whatever's already there so each arriving tier extends
        // the summary rather than replacing it. One ordering hazard: the light
        // `tables` response losing the race against `engagement` would replace
        // enriched rows with plain ones, so its table keys are dropped once
        // engagement data is in.
        const incoming: Partial<AnalyticsSummary> = { ...posthog }
        if (tier === 'tables' && entry.engagementLoaded) {
          delete incoming.topPages
          delete incoming.referrers
          delete incoming.devices
        }
        return {
          ...prev,
          [site]: {
            summary: posthog ? { ...entry.summary, ...incoming } : entry.summary,
            error: posthogError ?? entry.error,
            engagementLoaded:
              entry.engagementLoaded || (!!posthog && enrichesTables),
          },
        }
      })
      return { ok: true }
    },
    [lock],
  )

  // Progressive load for one site, most-visible-first: cards + chart (core),
  // the pageview tables' base columns (tables), the interaction panels
  // (interactions), and the tables' expensive time-on-site / scroll columns
  // (engagement). SEQUENTIAL on purpose: PostHog allows only a few concurrent
  // queries per project before throttling (429), so one tier's queries finish
  // before the next tier's fire — each panel still paints the moment its own
  // tier lands. Nothing renders off the returned promise — the panels show
  // loading states until fetchTier folds their data into siteData; it exists
  // only so loadEverything can sequence sites and the history sweep.
  const loadSite = useCallback(
    async (
      candidatePassword: string,
      site: AnalyticsSiteId,
      fetchRange: AnalyticsRange,
    ) => {
      for (const tier of ['core', 'tables', 'interactions', 'engagement'] as const) {
        // Stop early when the range changed or the user locked — the newer
        // loadEverything (or nobody) owns the remaining tiers.
        const stillCurrent = requestKeyRef.current.endsWith(
          `:${analyticsRangeKey(fetchRange)}`,
        )
        if (!stillCurrent) return
        await fetchTier(candidatePassword, site, fetchRange, tier).catch(() => {})
      }
    },
    [fetchTier],
  )

  // Sweep one site's entire history (launch → today) as a sequence of `history`
  // requests — each a single cheap daily-pageviews query — folding every
  // chunk's days into historyBySite as it lands, so the scrubber's readership
  // graph grows leftward toward launch. Sequential on purpose: this is
  // background garnish that shouldn't compete with user-triggered queries for
  // PostHog's capacity, and a throttled chunk is waited out and re-requested
  // rather than dropped. Any persistent failure just ends the sweep — a
  // partial graph is strictly better than none, and the next unlock retries
  // from scratch.
  const loadHistory = useCallback(
    async (candidatePassword: string, site: AnalyticsSite) => {
      if (historyStartedRef.current.has(site.id)) return
      historyStartedRef.current.add(site.id)
      for (const chunk of historyChunkRanges(site.launched)) {
        try {
          let result: Awaited<ReturnType<typeof fetchAnalytics>>
          let attempt = 0
          for (;;) {
            result = await fetchAnalytics(
              candidatePassword,
              site.id,
              chunk,
              'history',
            )
            if (requestKeyRef.current === 'locked') return
            if (!result.ok) {
              if (result.status === 401) lock()
              return
            }
            if (
              !isThrottledError(result.data.posthogError) ||
              attempt >= THROTTLE_RETRY_DELAYS_MS.length
            ) {
              break
            }
            await sleep(THROTTLE_RETRY_DELAYS_MS[attempt])
            attempt += 1
            if (requestKeyRef.current === 'locked') return
          }
          const points = result.data.posthog?.historyDaily
          if (!points) return
          setHistoryBySite(prev => {
            const byDay = new Map(
              (prev[site.id] ?? []).map(p => [p.day, p.pageviews]),
            )
            for (const point of points) byDay.set(point.day, point.pageviews)
            return {
              ...prev,
              [site.id]: [...byDay.entries()]
                .sort(([a], [b]) => (a < b ? -1 : 1))
                .map(([day, pageviews]) => ({ day, pageviews })),
            }
          })
        } catch {
          return
        }
      }
    },
    [lock],
  )

  // Kick off every panel of every site after auth, as ONE sequential queue:
  // the active site's tiers first (most-visible data soonest), then the other
  // sites' prefetches, then the full-history sweep (active site first). Never
  // more than one request in flight — PostHog throttles bursts of concurrent
  // queries (429), and a strict newest-work-first queue is what lets the
  // dashboard absorb arbitrarily large ranges: everything still arrives, just
  // paced at a rate PostHog accepts. Nothing here gates the UI — the
  // dashboard is already visible and each panel shows its own loading state.
  const loadEverything = useCallback(
    (
      candidatePassword: string,
      fetchRange: AnalyticsRange,
      activeSite: AnalyticsSiteId,
    ) => {
      void (async () => {
        await loadSite(candidatePassword, activeSite, fetchRange)
        for (const site of ANALYTICS_SITES) {
          if (site.id === activeSite || requestKeyRef.current === 'locked')
            continue
          await loadSite(candidatePassword, site.id, fetchRange)
        }
        if (requestKeyRef.current === 'locked') return
        const bySweepOrder = [...ANALYTICS_SITES].sort(
          (a, b) => Number(b.id === activeSite) - Number(a.id === activeSite),
        )
        for (const site of bySweepOrder) {
          await loadHistory(candidatePassword, site)
        }
      })()
    },
    [loadSite, loadHistory],
  )

  // Submitted from the password gate. A `verify` request confirms the password
  // WITHOUT waiting on any data: a 401 keeps the gate up with an error; success
  // reveals the dashboard immediately and kicks off every panel's load in the
  // background. This is what keeps login ungated on analytics data.
  const submitPassword = useCallback(
    async (candidatePassword: string) => {
      requestKeyRef.current = `:${analyticsRangeKey(range)}`
      const result = await fetchTier(candidatePassword, siteId, range, 'verify')
      if (result.ok) {
        writeStoredPassword(candidatePassword)
        setPassword(candidatePassword)
        loadEverything(candidatePassword, range, siteId)
      }
      return { ok: result.ok, message: result.message }
    },
    [range, siteId, fetchTier, loadEverything],
  )

  useEffect(() => {
    // Restore site / range / sorts from the URL before anything fetches, so a
    // shared link opens on exactly the view it was copied from. (This runs
    // client-side only; the pre-boot render is null either way.)
    const parsed = parseUrlState(window.location.search)
    if (parsed.site) setSiteId(parsed.site)
    if (parsed.range) setRange(parsed.range)
    if (Object.keys(parsed.sorts).length > 0) {
      setSorts({ ...defaultSorts(), ...parsed.sorts })
    }
    const initialSite = parsed.site ?? DEFAULT_ANALYTICS_SITE_ID
    const initialRange = parsed.range ?? DEFAULT_RANGE

    const stored = readStoredPassword()
    if (stored) {
      requestKeyRef.current = `:${analyticsRangeKey(initialRange)}`
      // Reveal on a successful verify, then stream the panels in. On a stale
      // stored password the verify 401s and fetchTier's lock() drops the gate.
      void fetchTier(stored, initialSite, initialRange, 'verify').then(result => {
        if (result.ok) {
          setPassword(stored)
          loadEverything(stored, initialRange, initialSite)
        }
      })
    }
    setHasBooted(true)
  }, [fetchTier, loadEverything])

  // Mirror the view state into the URL so every variant is shareable /
  // reloadable. replaceState (not router navigation) because this is pure
  // view state — no server round-trip, no history spam from toggling sorts.
  useEffect(() => {
    if (!hasBooted) return
    const search = buildUrlSearch(siteId, range, sorts)
    window.history.replaceState(
      null,
      '',
      search ? `${window.location.pathname}?${search}` : window.location.pathname,
    )
  }, [hasBooted, siteId, range, sorts])

  const handleSiteChange = (nextSite: AnalyticsSiteId) => {
    if (nextSite === siteId) return
    setSiteId(nextSite)
    // The background prefetch usually means the target already has data; only
    // (re)fetch if it's genuinely empty for the current range.
    if (password && !siteData[nextSite].summary) {
      void loadSite(password, nextSite, range)
    }
  }

  const applyRange = (nextRange: AnalyticsRange) => {
    if (analyticsRangeKey(nextRange) === analyticsRangeKey(range) || !password)
      return
    setRange(nextRange)
    // Numbers change with the range, so every site's cache is now stale. Reset
    // and reload: the active site as per-panel tiers, the rest as background
    // prefetch.
    requestKeyRef.current = `:${analyticsRangeKey(nextRange)}`
    setSiteData(emptySiteData())
    loadEverything(password, nextRange, siteId)
  }

  const handleSortChange = (panel: PanelId) => (sort: SortState) =>
    setSorts(prev => ({ ...prev, [panel]: sort }))

  if (!hasBooted) return null
  if (!password) return <PasswordGate onSubmit={submitPassword} />

  const entry = siteData[siteId]
  const summary = entry.summary
  const hasCore = !!summary?.totals
  const activeError = entry.error
  const uniqueShare =
    summary?.totals && summary.totals.pageviews > 0
      ? (summary.totals.visitors / summary.totals.pageviews) * 100
      : 0

  // Denominator the two interaction panels share (see comparisonVisitors);
  // arrives with the interactions tier. Falls back to the range total.
  const interactionVisitors =
    summary?.comparisonVisitors ?? summary?.totals?.visitors ?? 0

  // Whether the active site gets the plan-exits panel (its front page is the
  // choose-your-path playbook). The panel's data rides the engagement tier.
  const showPlanExits =
    (getAnalyticsSite(siteId) ?? ANALYTICS_SITES[0]).hasPlanChoices

  // Tab / sort-header anchor hrefs: the URL the click would produce,
  // so cmd-click opens that view in a new tab (see isPlainLeftClick).
  const hrefForSearch = (search: string) =>
    search ? `/analytics?${search}` : '/analytics'
  const siteHref = (site: AnalyticsSiteId) =>
    hrefForSearch(buildUrlSearch(site, range, sorts))
  const sortHrefFor = (panel: PanelId) => (sort: SortState) =>
    hrefForSearch(buildUrlSearch(siteId, range, { ...sorts, [panel]: sort }))

  return (
    <div className="flex flex-col gap-3 font-system-ui text-[13px]">
      {/* One tab per website (each its own PostHog project), with the PostHog
          link and Lock button riding the same row on the right. The whole
          dashboard below renders for whichever site is active. Switching shows
          that site's already-prefetched data instantly, or loads it if not
          ready yet. */}
      <div className="flex items-center justify-between border-b border-neutral-300">
        <div role="tablist" aria-label="Website" className="flex gap-px">
          {ANALYTICS_SITES.map(site => (
            <a
              key={site.id}
              role="tab"
              aria-selected={site.id === siteId}
              href={siteHref(site.id)}
              onClick={e => {
                if (!isPlainLeftClick(e)) return
                e.preventDefault()
                handleSiteChange(site.id)
              }}
              className={`-mb-px border-b-2 px-4 py-1.5 text-[13px] font-medium no-underline ${
                site.id === siteId
                  ? 'border-[#2A623D] text-[#2A623D]'
                  : 'border-transparent text-[#666] hover:text-neutral-900'
              }`}
            >
              {site.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <a
            href={POSTHOG_UI_HOST}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-[#2A623D] hover:underline"
          >
            PostHog <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={lock}
            className="text-[#666] hover:underline"
          >
            Lock
          </button>
        </div>
      </div>

      {/* The date-range control: the site's whole history as a scrubbable
          timeline. Dragging edits a local draft inside the component; only a
          released selection reaches applyRange (which refetches everything).
          The range is shared across site tabs, so switching to an older site
          widens the timeline while keeping the selected window. `history` is
          the background full-history sweep's daily pageviews, drawn as a
          readership graph behind the track once it streams in. */}
      <TimelineRangeScrubber
        siteLaunched={(getAnalyticsSite(siteId) ?? ANALYTICS_SITES[0]).launched}
        value={range}
        maxDays={MAX_ANALYTICS_RANGE_DAYS}
        onChange={applyRange}
        history={historyBySite[siteId]}
      />

      {/* Any query failure surfaces as this notice, but it only REPLACES the
          panels when the site has no data at all (e.g. no personal key
          configured, so nothing can ever load). If some tiers already landed,
          the notice rides above the loaded panels — one throttled query
          shouldn't blank out everything that succeeded. */}
      {activeError && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-semibold">PostHog not queryable yet: </span>
          {activeError}
        </div>
      )}
      {(!activeError || summary) && (
        <>
          {/* Summary cards — real values once core lands, per-card skeletons
              until then. */}
          {hasCore && summary?.totals ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SummaryCard label="Pageviews" value={summary.totals.pageviews} />
              <SummaryCard
                label="Unique visitors"
                value={summary.totals.visitors}
                sub={`${uniqueShare.toFixed(0)}% of views`}
              />
              <SummaryCard
                label="Pageviews / day"
                value={Math.round(summary.totals.pageviews / rangeDayCount(range))}
              />
              <SummaryCard
                label="Views / visitor"
                value={Math.round(
                  (summary.totals.pageviews /
                    Math.max(1, summary.totals.visitors)) *
                    10,
                ) / 10}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {SUMMARY_CARD_LABELS.map(label => (
                <SummaryCardSkeleton key={label} label={label} />
              ))}
            </div>
          )}

          {/* Daily chart — the section is always here; its body shows a loading
              placeholder until core's daily data arrives. */}
          <section className="border border-neutral-200 bg-white px-3 py-2.5">
            <h2 className="mb-2 mt-0 text-[13px] font-semibold">
              Daily traffic —{' '}
              {range.kind === 'days'
                ? `last ${range.days} days`
                : `${range.start} to ${range.end}`}
            </h2>
            {hasCore && summary?.daily ? (
              <DailyBarChart daily={summary.daily} />
            ) : (
              <div
                className="h-44 animate-pulse rounded-sm bg-neutral-100"
                aria-hidden="true"
              />
            )}
          </section>

          {/* Pageview tables — the base columns render as soon as the light
              `tables` tier lands (plus core's totals, the % denominator, which
              arrives around the same moment); the expensive time-on-site /
              scroll columns shimmer until the `engagement` tier folds them in.
              Stacked full-width to give the six-column tables room; a
              half-width grid cell would crush the label column. */}
          {summary?.topPages && summary.totals ? (
            <div className="flex flex-col gap-2">
              <BreakdownTable
                title="Top pages"
                countLabel="pageviews"
                rows={summary.topPages}
                totalVisitors={summary.totals.visitors}
                sort={sorts.pages}
                onSortChange={handleSortChange('pages')}
                sortHref={sortHrefFor('pages')}
                engagementPending={!entry.engagementLoaded}
              />
              {/* Front-page exits split by the plan open when the visitor
                  left. This exists because the front page's scrollable length
                  changes several-fold as plans open, so the "/" row's blended
                  scroll average mixes incomparable page states; these rows
                  each measure scroll within ONE page state (see the
                  plan-exits query in posthog.ts). Data rides the engagement
                  tier, hence the skeleton until it lands. */}
              {showPlanExits &&
                (summary.planExits ? (
                  <div className="flex flex-col gap-1">
                    <BreakdownTable
                      title="Front page — plan open at exit"
                      countLabel="page-exits"
                      rows={summary.planExits}
                      totalVisitors={summary.totals.visitors}
                      sort={sorts.planExits}
                      onSortChange={handleSortChange('planExits')}
                      sortHref={sortHrefFor('planExits')}
                      durationSampleNoun="page-exits"
                    />
                    <p className="mb-0 px-1.5 text-[11px] leading-snug text-[#888]">
                      Front-page exits grouped by the plan in the exit URL&apos;s
                      ?choices= param — the plan open when the visitor left
                      (switching plans re-buckets the visit to the last one).
                      Each plan selection restarts PostHog&apos;s scroll
                      measurement, so scroll depth here reads against that final
                      page state: plan rows against the fully expanded plan
                      document, &quot;No plan opened&quot; against the short
                      pre-choice page.
                      {summary.planExitsExcluded ? (
                        <>
                          {' '}
                          Excludes {summary.planExitsExcluded}{' '}
                          {summary.planExitsExcluded === 1
                            ? 'page-exit'
                            : 'page-exits'}{' '}
                          whose ?choices= value was mangled in transit (privacy
                          proxies rewriting URLs) — a plan was open, but not
                          reliably identifiable.
                        </>
                      ) : null}
                    </p>
                  </div>
                ) : (
                  <PanelSkeleton title="Front page — plan open at exit" />
                ))}
              <BreakdownTable
                title="Referrers"
                countLabel="pageviews"
                rows={summary.referrers ?? []}
                totalVisitors={summary.totals.visitors}
                sort={sorts.referrers}
                onSortChange={handleSortChange('referrers')}
                sortHref={sortHrefFor('referrers')}
                engagementPending={!entry.engagementLoaded}
              />
              <BreakdownTable
                title="Devices"
                countLabel="pageviews"
                rows={summary.devices ?? []}
                totalVisitors={summary.totals.visitors}
                sort={sorts.devices}
                onSortChange={handleSortChange('devices')}
                sortHref={sortHrefFor('devices')}
                maxRows={10}
                engagementPending={!entry.engagementLoaded}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <PanelSkeleton title="Top pages" />
              {showPlanExits && (
                <PanelSkeleton title="Front page — plan open at exit" />
              )}
              <PanelSkeleton title="Referrers" />
              <PanelSkeleton title="Devices" />
            </div>
          )}

          {/* Interaction panels — gated only on their own `interactions` tier
              (plus totals for the no-hovers denominator fallback), independent
              of the pageview tables above. */}
          {summary?.hovers && summary.hudClicks && summary.totals ? (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <BreakdownTable
                title="Hovered elements"
                countLabel="hovers"
                rows={summary.hovers}
                // Hovers and HUD clicks share the comparison-window denominator
                // (visitors since hover tracking began) so their % columns are
                // directly comparable — see AnalyticsSummary.comparisonVisitors.
                // Falls back to the range total only when no hover data exists.
                totalVisitors={interactionVisitors}
                sort={sorts.hovers}
                onSortChange={handleSortChange('hovers')}
                sortHref={sortHrefFor('hovers')}
                emptyMessage="No hover events yet — hover tracking ships alongside this dashboard, so data starts accruing once it's deployed."
              />
              <BreakdownTable
                title="HUD clicks"
                countLabel="clicks"
                rows={summary.hudClicks}
                // Same comparison-window denominator as the hovers panel above,
                // so a click % and a hover % are fractions of the same pool.
                totalVisitors={interactionVisitors}
                sort={sorts.hudClicks}
                onSortChange={handleSortChange('hudClicks')}
                sortHref={sortHrefFor('hudClicks')}
                maxRows={30}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <PanelSkeleton title="Hovered elements" />
              <PanelSkeleton title="HUD clicks" />
            </div>
          )}
        </>
      )}
    </div>
  )
}
